import React from "react";
import { CalendarCheck, ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatZonedDate, formatZonedTime, getLocalTimezone } from "@/lib/eventDatetime";
import type { PendingBreakfastInvite } from "@/features/breakfast/types";

interface BreakfastInviteCardProps {
  invite: PendingBreakfastInvite;
  compact: boolean;
  isLoading: boolean;
  onAccept: (eventId: string) => void;
  onDecline: (eventId: string) => void;
  /**
   * When `true`, the card renders with an amber "Rescheduled" chip and
   * adjusted copy (`"has changed — please re-confirm"`) so the guest
   * understands *why* they're being asked to RSVP again. Driven by the
   * presence of a matching `EVENT_UPDATE` notification for this event.
   */
  rescheduled?: boolean;
}

/**
 * "Push notification"-style RSVP prompt for a pending breakfast invite.
 *
 * Sourced from `usePendingBreakfastInvites` (not the generic
 * `notifications` table) so the card is available even when the upstream
 * BREAKFAST_INVITE row never landed — a defence-in-depth for flaky invite-
 * notification pipelines.
 *
 * Kept as thin wrappers around the shared TZ-aware helpers so the rest of
 * the card renders with a familiar call-site shape. Everything goes through
 * the viewer's local zone — for invite previews we intentionally do NOT
 * show the "(your time: …)" helper, since the full cross-TZ breakdown
 * lives one tap away in the Pending Invites sidebar.
 */
export function BreakfastInviteCard({
  invite,
  compact,
  isLoading,
  onAccept,
  onDecline,
  rescheduled = false,
}: BreakfastInviteCardProps) {
  const hostName =
    [invite.host?.first_name, invite.host?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    invite.host?.email ||
    "Someone";

  return (
    <div
      className={cn(
        "transition-opacity",
        compact ? "px-4 py-3" : "px-5 py-4",
        isLoading && "opacity-40 pointer-events-none",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
            rescheduled ? "bg-amber-500/10" : "bg-orange-500/10",
          )}
        >
          <CalendarCheck
            className={cn(
              "h-4 w-4",
              rescheduled ? "text-amber-500" : "text-orange-500",
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p
              className={cn(
                "font-semibold leading-tight truncate",
                compact ? "text-xs" : "text-sm",
              )}
            >
              {invite.title}
            </p>
            {rescheduled && (
              <span
                className={cn(
                  "shrink-0 rounded-full font-bold uppercase tracking-wide",
                  "bg-amber-500/15 text-amber-600 dark:text-amber-400",
                  compact
                    ? "text-[9px] px-1.5 py-[1px]"
                    : "text-[10px] px-2 py-0.5",
                )}
              >
                Rescheduled
              </span>
            )}
          </div>
          <p
            className={cn(
              "text-muted-foreground mt-0.5 leading-relaxed",
              compact ? "text-[11px]" : "text-xs",
            )}
          >
            {rescheduled
              ? "Time changed — please re-confirm · "
              : `Hosted by ${hostName} · `}
            {formatZonedDate(invite.starts_at, getLocalTimezone())} ·{" "}
            {formatZonedTime(invite.starts_at, getLocalTimezone())}
          </p>
          <div className="flex gap-1.5 mt-2.5">
            <Button
              size="sm"
              className={cn(
                "gap-1 font-semibold",
                compact ? "h-6 text-[10px] px-2" : "h-7 text-xs px-3",
              )}
              disabled={isLoading}
              onClick={() => onAccept(invite.id)}
            >
              <ThumbsUp className="h-3 w-3" />
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "gap-1 font-medium text-destructive hover:bg-destructive/10",
                compact ? "h-6 text-[10px] px-2" : "h-7 text-xs px-3",
              )}
              disabled={isLoading}
              onClick={() => onDecline(invite.id)}
            >
              <ThumbsDown className="h-3 w-3" />
              Decline
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
